import React, { useRef, useState, lazy, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTenant } from '../../hooks/useTenant';
import { useAuth } from '../../context/AuthContext';
import BookingCalendar from '../../components/BookingCalendar';
import ReviewsSection from '../../components/ReviewsSection';
import {
  Service,
  TenantTimeOff,
  createAppointment,
  updateCustomerProfile,
  ApiError,
} from '../../api';
import { format, addDays, parseISO } from 'date-fns';
import {
  Clock,
  MapPin,
  Star,
  ArrowLeft,
  Phone,
  Instagram,
  Loader2,
  LogIn,
  MailCheck,
  CalendarOff,
} from 'lucide-react';
import { formatSalon, salonNow } from '../../lib/datetime';
import PhoneField from '../../components/PhoneField';
import PhotoGallery from '../../components/PhotoGallery';
const LocationMap = lazy(() => import('../../components/LocationMap'));

/**
 * The closure's wall-clock bounds as sortable "yyyy-MM-dd HH:mm" strings.
 *
 * Mirrors the backend's `time_off_window`: a null start runs from midnight on
 * `startDate`, a null end runs through the *end* of `endDate`, which is inclusive.
 * Fixed-width fields make lexical order chronological order, and "24:00" sorts
 * after every real time, so plain string compares are enough here.
 */
function closureBounds(closure: TenantTimeOff): { start: string; end: string } {
  return {
    start: `${closure.startDate} ${closure.startTime ?? '00:00'}`,
    end: `${closure.endDate} ${closure.endTime ?? '24:00'}`,
  };
}

/**
 * "on Mon 10 Aug" / "from Mon 10 Aug to Thu 20 Aug" / "from Mon 10 Aug 14:00 to
 * Wed 12 Aug 09:00" — plain dates, parsed without a timezone shift.
 *
 * The API announces a block once it swallows any *one* whole day, so the first
 * and last day can still be partial — "10 Aug 14:00 -> 12 Aug 09:00" is published
 * because the 11th is whole. Dropping those edge times would tell a customer the
 * salon is shut on the morning of the 10th while it trades until 14:00. A null
 * time is a real whole day and needs no clock: the bare date already says it.
 */
function describeClosure(closure: TenantTimeOff): string {
  const day = (iso: string) => format(parseISO(iso), 'EEE d MMM');
  const from = closure.startTime
    ? `${day(closure.startDate)} ${closure.startTime}`
    : day(closure.startDate);
  const to = closure.endTime ? `${day(closure.endDate)} ${closure.endTime}` : day(closure.endDate);
  if (from === to) return `on ${from}`;
  return `from ${from} to ${to}`;
}

export default function ShopProfile() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { tenant, services, loading } = useTenant(slug);
  const { session, openAuth, refresh } = useAuth();

  const [selectedService, setSelectedService] = useState<Service | null>(null);
  // A specific worker id, or 'any' (the default) for first available.
  const [selectedStaff, setSelectedStaff] = useState<number | 'any'>('any');
  const [selectedTime, setSelectedTime] = useState<Date | null>(null);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Phone capture for OAuth signups that have no phone yet.
  const [phoneInput, setPhoneInput] = useState<string | undefined>();
  const [savingPhone, setSavingPhone] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center">Loading...</div>;
  }

  if (!tenant) {
    return <div className="flex min-h-screen items-center justify-center">Shop not found.</div>;
  }

  const isCustomer = session?.role === 'customer';
  const emailVerified = Boolean(session?.emailVerified);
  const hasPhone = Boolean(session?.customer?.phone);

  // The hero's back arrow only earns its place in the installed PWA, which runs
  // standalone with no browser chrome to go back with. In a browser it merely
  // duplicates the toolbar, and on the page's most common entry — a salon
  // sharing its own /shop/:slug link — there is no directory behind us at all,
  // so history.back() would eject the visitor from the site. React Router stamps
  // an index onto each entry it pushes; anything above 0 means we arrived from
  // another Calendyz page and have somewhere of our own to return to.
  const canGoBack = ((window.history.state as { idx?: number } | null)?.idx ?? 0) > 0;

  // Workers at this salon who perform the selected service (drives the selector).
  const qualifiedStaff = selectedService
    ? tenant.staff.filter((w) => w.serviceIds.includes(selectedService.id))
    : [];

  // The nearest salon-wide closure worth warning about: on now, or starting
  // within the two weeks the booking strip covers. Only one is shown — a wall of
  // future closures is noise on a page whose job is to take a booking.
  const nowStr = format(salonNow(), 'yyyy-MM-dd HH:mm');
  const horizonStr = `${format(addDays(salonNow(), 14), 'yyyy-MM-dd')} 24:00`;
  const upcomingClosure = tenant.timeOff
    .filter((t) => closureBounds(t).end >= nowStr && closureBounds(t).start <= horizonStr)
    .sort((a, b) => closureBounds(a).start.localeCompare(closureBounds(b).start))[0];
  // Against the clock, not just the date: a block starting at 14:00 today has not
  // started at 09:00, and "Closed right now" would be a lie until it does.
  const closureHasStarted = Boolean(
    upcomingClosure && closureBounds(upcomingClosure).start <= nowStr,
  );

  const handleConfirmBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedService || !selectedTime || !slug) return;

    setSubmitting(true);
    setError(null);
    try {
      await createAppointment(slug, {
        serviceId: selectedService.id,
        startTime: selectedTime.toISOString(),
        staffId: selectedStaff === 'any' ? undefined : selectedStaff,
      });
      setBookingComplete(true);
    } catch (err: unknown) {
      const apiErr = err as { body?: { startTime?: string[]; detail?: string } };
      setError(
        apiErr.body?.startTime?.[0] ||
          apiErr.body?.detail ||
          'Failed to create booking. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSavePhone = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phoneInput) return;
    setSavingPhone(true);
    setError(null);
    try {
      await updateCustomerProfile({ phone: phoneInput });
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save your phone number.');
    } finally {
      setSavingPhone(false);
    }
  };

  if (bookingComplete) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center">
        <div className="bg-success-soft text-success mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full">
          <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-fg mb-4 text-3xl font-bold">Booking Confirmed!</h2>
        <p className="text-fg-secondary mb-8 text-lg">
          You're all set for {selectedService?.name} at {tenant.name} on{' '}
          {selectedTime && formatSalon(selectedTime, 'MMMM d, yyyy h:mm a')}.
        </p>
        <div className="flex justify-center gap-3">
          <button
            onClick={() => navigate('/account/bookings')}
            className="bg-brand-600 hover:bg-brand-700 rounded-xl px-6 py-3 font-medium text-white transition-colors"
          >
            View My Bookings
          </button>
          <button
            onClick={() => navigate('/')}
            className="border-strong bg-surface text-fg-secondary hover:bg-canvas rounded-xl border px-6 py-3 font-medium transition-colors"
          >
            Back to Directory
          </button>
        </div>
      </div>
    );
  }

  /** The gated action shown once a service + time are picked. */
  const renderBookingAction = () => {
    if (!isCustomer) {
      return (
        <button
          type="button"
          onClick={() => openAuth('login')}
          className="bg-brand-600 hover:bg-brand-700 flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 font-medium text-white transition-colors"
        >
          <LogIn className="h-4 w-4" />
          Log in to book
        </button>
      );
    }
    if (!emailVerified) {
      return (
        <div className="space-y-3">
          <div className="border-warn/30 bg-warn-soft text-warn flex items-start gap-2 rounded-xl border p-3 text-sm">
            <MailCheck className="mt-0.5 h-4 w-4 shrink-0" />
            Verify your email address to confirm bookings.
          </div>
          <button
            type="button"
            onClick={() => openAuth('verify')}
            className="bg-brand-600 hover:bg-brand-700 w-full rounded-xl px-4 py-3 font-medium text-white transition-colors"
          >
            Verify my email
          </button>
        </div>
      );
    }
    if (!hasPhone) {
      return (
        <form onSubmit={handleSavePhone} className="space-y-3">
          <p className="text-fg-secondary text-sm">Add a phone number to complete your booking.</p>
          <PhoneField
            value={phoneInput}
            onChange={setPhoneInput}
            selectClassName="rounded-xl border border-strong shadow-sm px-3 py-3 text-sm"
            inputClassName="flex-1 rounded-xl border border-strong shadow-sm px-4 py-3 text-sm outline-none focus:border-brand-600 focus:ring-1 focus:ring-brand-600"
          />
          <button
            type="submit"
            disabled={savingPhone || !phoneInput}
            className="bg-brand-600 hover:bg-brand-700 flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 font-medium text-white transition-colors disabled:opacity-50"
          >
            {savingPhone && <Loader2 className="h-4 w-4 animate-spin" />}
            Save phone number
          </button>
        </form>
      );
    }
    return (
      <form onSubmit={handleConfirmBooking} className="flex space-x-4">
        <button
          type="button"
          onClick={() => setSelectedTime(null)}
          className="border-strong bg-surface text-fg-secondary hover:bg-canvas flex-1 rounded-xl border px-4 py-3 font-medium transition-colors"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="bg-brand-600 hover:bg-brand-700 flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-3 font-medium text-white transition-colors disabled:opacity-50"
        >
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          {submitting ? 'Booking...' : 'Confirm Booking'}
        </button>
      </form>
    );
  };

  return (
    <div className="pb-20">
      {/* Hero */}
      <div className="relative">
        {tenant.photos && tenant.photos.length > 0 ? (
          <PhotoGallery photos={tenant.photos} />
        ) : (
          <div className="from-muted to-muted-strong flex h-64 items-center justify-center bg-gradient-to-br sm:h-80 lg:h-96">
            <span className="text-fg-faint text-7xl">{tenant.name.charAt(0)}</span>
          </div>
        )}
        {canGoBack && (
          <div className="absolute top-4 left-4 z-10">
            <button
              onClick={() => navigate(-1)}
              aria-label="Go back"
              className="rounded-full bg-white/20 p-2 text-white backdrop-blur-md transition-colors hover:bg-white/30"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
          </div>
        )}
        <div className="absolute right-0 bottom-0 left-0 z-10 bg-gradient-to-t from-black/60 to-transparent p-6 sm:p-8">
          <div className="mx-auto max-w-7xl">
            <h1 className="mb-2 text-3xl font-bold text-white sm:text-5xl">{tenant.name}</h1>
            <div className="flex items-center gap-4 text-sm text-white/90 sm:text-base">
              <span className="flex items-center">
                <MapPin className="mr-1 h-4 w-4" /> {tenant.address}
              </span>
              <span className="flex items-center">
                <Star className="mr-1 h-4 w-4 fill-current text-yellow-400" /> {tenant.rating}
                {tenant.reviewCount > 0 && (
                  <span className="ml-1 text-white/70">({tenant.reviewCount})</span>
                )}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 py-8 sm:px-6 lg:grid-cols-3 lg:px-8">
        {/* Main Content */}
        <div className="space-y-10 lg:col-span-2">
          {/* Sits above the booking flow, not beside the read-only hours in the
              sidebar, where a customer scrolling to book would never see it. */}
          {upcomingClosure && (
            <div className="border-warn/30 bg-warn-soft flex items-start gap-3 rounded-xl border p-4">
              <CalendarOff className="text-warn mt-0.5 h-5 w-5 shrink-0" />
              <div>
                <p className="text-warn text-sm font-semibold">
                  {closureHasStarted ? 'Closed right now' : 'Closing soon'}
                </p>
                <p className="text-warn text-sm">
                  {tenant.name} is closed {describeClosure(upcomingClosure)}.
                </p>
              </div>
            </div>
          )}

          {tenant.description ? (
            <section>
              <h2 className="font-display text-fg mb-4 text-2xl font-semibold">About</h2>
              <p className="text-fg-secondary leading-relaxed">{tenant.description}</p>
            </section>
          ) : null}

          <section>
            <h2 className="font-display text-fg mb-6 text-2xl font-semibold">Services</h2>
            <div className="space-y-4">
              {services.map((service) => (
                <div
                  key={service.id}
                  onClick={() => {
                    setSelectedService(service);
                    setSelectedStaff('any');
                    setSelectedTime(null);
                    setTimeout(() => {
                      calendarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 100);
                  }}
                  className={`cursor-pointer rounded-2xl border-2 p-4 transition-all ${
                    selectedService?.id === service.id
                      ? 'border-brand-600 bg-accent-soft'
                      : 'border-subtle bg-surface hover:border-default'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-fg text-lg font-semibold">{service.name}</h3>
                      <p className="text-fg-muted mt-1 flex items-center text-sm">
                        <Clock className="mr-1 h-4 w-4" /> {service.duration} mins
                      </p>
                    </div>
                    <div className="text-fg text-lg font-bold">${service.price}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {selectedService && !selectedTime && (
            <div ref={calendarRef} className="space-y-6">
              {qualifiedStaff.length > 0 && (
                <div className="border-default bg-surface rounded-2xl border p-6 shadow-sm">
                  <h3 className="text-fg mb-4 text-lg font-semibold">Choose a Professional</h3>
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => setSelectedStaff('any')}
                      className={`flex items-center gap-2 rounded-xl border-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                        selectedStaff === 'any'
                          ? 'border-brand-600 bg-accent-soft text-accent'
                          : 'border-default bg-surface text-fg-secondary hover:border-strong'
                      }`}
                    >
                      Any available
                    </button>
                    {qualifiedStaff.map((worker) => (
                      <button
                        key={worker.id}
                        type="button"
                        onClick={() => setSelectedStaff(worker.id)}
                        className={`flex items-center gap-2 rounded-xl border-2 px-3 py-2 text-sm font-medium transition-colors ${
                          selectedStaff === worker.id
                            ? 'border-brand-600 bg-accent-soft text-accent'
                            : 'border-default bg-surface text-fg-secondary hover:border-strong'
                        }`}
                      >
                        {worker.photo ? (
                          <img
                            src={worker.photo}
                            alt={worker.name}
                            className="h-7 w-7 rounded-full object-cover"
                          />
                        ) : (
                          <span className="bg-accent-soft text-accent flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold">
                            {worker.name.charAt(0).toUpperCase()}
                          </span>
                        )}
                        {worker.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <BookingCalendar
                tenant={tenant}
                service={selectedService}
                staffId={selectedStaff}
                onSelectTime={setSelectedTime}
              />
            </div>
          )}

          {selectedService && selectedTime && (
            <section className="border-default bg-canvas rounded-2xl border p-6">
              <h3 className="text-fg mb-4 text-xl font-bold">Complete Booking</h3>
              <div className="border-subtle bg-surface mb-6 rounded-xl border p-4">
                <p className="text-fg font-medium">{selectedService.name}</p>
                <p className="text-fg-muted mt-1 text-sm">
                  {formatSalon(selectedTime, 'EEEE, MMMM d, yyyy')} at{' '}
                  {formatSalon(selectedTime, 'h:mm a')}
                </p>
                {selectedStaff !== 'any' && (
                  <p className="text-fg-muted mt-1 text-sm">
                    with {qualifiedStaff.find((w) => w.id === selectedStaff)?.name}
                  </p>
                )}
              </div>

              {error && (
                <div className="border-danger/25 bg-danger-soft text-danger mb-4 rounded-xl border p-3 text-sm">
                  {error}
                </div>
              )}

              {renderBookingAction()}
            </section>
          )}

          {slug && (
            <ReviewsSection slug={slug} rating={tenant.rating} reviewCount={tenant.reviewCount} />
          )}
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="sticky top-6 space-y-4">
            <div className="border-default bg-surface rounded-2xl border p-6">
              <h3 className="text-fg mb-4 text-lg font-bold">Business Hours</h3>
              <ul className="space-y-3 text-sm">
                {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(
                  (day) => {
                    const hours = tenant.businessHours[day];
                    return (
                      <li key={day} className="flex justify-between">
                        <span className="text-fg-muted">{day}</span>
                        <span className="text-fg font-medium">
                          {hours ? `${hours.open} - ${hours.close}` : 'Closed'}
                        </span>
                      </li>
                    );
                  },
                )}
              </ul>
            </div>
            {(tenant.phoneNumber || tenant.instagramHandle) && (
              <div className="border-default bg-surface rounded-2xl border p-6">
                <h3 className="text-fg mb-4 text-lg font-bold">Contact</h3>
                <div className="space-y-3">
                  {tenant.phoneNumber && (
                    <div className="flex items-center gap-3">
                      <a
                        href={`tel:${tenant.phoneNumber}`}
                        className="text-fg-secondary hover:text-fg flex items-center gap-2 text-sm transition-colors"
                      >
                        <Phone className="text-fg-subtle h-4 w-4" />
                        {tenant.phoneNumber}
                      </a>
                      {tenant.isWhatsapp && (
                        <a
                          href={`https://wa.me/${tenant.phoneNumber.replace(/[^0-9]/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-success-soft text-success hover:bg-success-soft flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium transition-colors"
                        >
                          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                          </svg>
                          WhatsApp
                        </a>
                      )}
                    </div>
                  )}
                  {tenant.instagramHandle && (
                    <a
                      href={`https://instagram.com/${tenant.instagramHandle}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-fg-secondary hover:text-fg flex items-center gap-2 text-sm transition-colors"
                    >
                      <Instagram className="text-fg-subtle h-4 w-4" />@{tenant.instagramHandle}
                    </a>
                  )}
                </div>
              </div>
            )}
            {tenant.latitude && tenant.longitude && (
              <Suspense
                fallback={
                  <div className="bg-muted h-[180px] animate-pulse rounded-xl sm:h-[200px]" />
                }
              >
                <LocationMap latitude={tenant.latitude} longitude={tenant.longitude} />
              </Suspense>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
